/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Sourcing events and the
 * RFP-2026-014 (Pillar Pro LED high-bay fixtures 2027-2029) bid data. Bidders
 * and numbers follow the README: unit / delivered per fixture of $83.70 / $83.70
 * (Aurelia), $85.03 / $85.03 (Northgate), $86.70 / $88.85 (Hanover) and
 * $81.80 / $109.14 (Brightpak).
 */
import { getMaterial, materialSuppliers } from '../../data/materials';
import { getSupplier } from '../../data/suppliers';
import {
  evaluateScenario,
  landedCostScore,
  leadTimeScore,
} from './sourcing-scoring';
import type { CriterionId, Scores } from './sourcing-scoring';
import { fmtFixtures, fmtPerUnit } from './sourcing-format';
import { BIDDER_IDS } from './sourcing-types';
import type {
  BidderId,
  Shares,
  SourcingEvent,
  SourcingStatus,
} from './sourcing-types';

export const RFP_ID = 'RFP-2026-014';
export const RFQ_ID = 'RFQ-2026-022';

// ---------------------------------------------------------------------------
// Baseline
// ---------------------------------------------------------------------------

const LED = getMaterial('MAT-LED');

/** Annual volume in fixtures (130,000). */
export const LED_VOLUME = LED.annualVolume;
export const CONTRACT_YEARS = 3;

/** Current blended run-rate per fixture: 68% Aurelia at $88.70, 32% Northgate at $89.95. */
export const BASELINE_PER_UNIT = materialSuppliers('MAT-LED').reduce(
  (sum, m) => sum + (m.share / 100) * m.unitPrice,
  0,
);
/** About $11.58M per year. */
export const BASELINE_ANNUAL = BASELINE_PER_UNIT * LED_VOLUME;
export const BASELINE_THREE_YEAR = BASELINE_ANNUAL * CONTRACT_YEARS;

// ---------------------------------------------------------------------------
// Bids
// ---------------------------------------------------------------------------

export type UflpaStatus = 'clear' | 'flagged';

export interface BidFacts {
  supplierId: BidderId;
  roleLabel: string;
  /** Days from today; negative for the past. */
  receivedOffsetDays: number;
  incoterm: string;
  /** Per fixture. */
  unitPrice: number;
  freight: number;
  tariff: number;
  tariffPct: number;
  /** Minimum order quantity per release, in fixtures. */
  moqUnits: number;
  leadTimeWeeks: number;
  paymentNetDays: number;
  paymentExtra?: string;
  priceValidityDays: number;
  priceHoldYears: number;
  priceHoldNote: string;
  /** Fixtures per year dedicated to the Pillar Pro program. */
  capacityUnits: number;
  changeNoticeDays: number;
  ul1598: boolean;
  dlcPremium: boolean;
  iso9001: boolean;
  certNote?: string;
  warrantyYears: number;
  /** Hours until light output falls to 70% (L70). */
  l70Hours: number;
  recycledPct: number;
  recyclabilityLabel: string;
  uflpa: UflpaStatus;
  uflpaDetail: string;
  /** Optional fees quoted on top of the unit price, per year. */
  optionalFeesAnnual: number;
  feesLabel: string;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export const BIDS: Record<BidderId, BidFacts> = {
  'sup-aurelia': {
    supplierId: 'sup-aurelia',
    roleLabel: 'Incumbent, 68% share',
    receivedOffsetDays: -12,
    incoterm: 'Delivered, freight included',
    unitPrice: 83.7,
    freight: 0,
    tariff: 0,
    tariffPct: 0,
    moqUnits: 2_500,
    leadTimeWeeks: 6,
    paymentNetDays: 60,
    paymentExtra: '2% off if paid within 10 days',
    priceValidityDays: 180,
    priceHoldYears: 3,
    priceHoldNote: 'Adjusts with the aluminium market price',
    capacityUnits: 150_000,
    changeNoticeDays: 180,
    ul1598: true,
    dlcPremium: true,
    iso9001: true,
    warrantyYears: 10,
    l70Hours: 150_000,
    recycledPct: 25,
    recyclabilityLabel: 'Foam-free cartons by 2028',
    uflpa: 'clear',
    uflpaDetail:
      'Supply chain mapped three steps back, North American and Gulf aluminium',
    optionalFeesAnnual: 0,
    feesLabel: 'None quoted',
  },
  'sup-northgate': {
    supplierId: 'sup-northgate',
    roleLabel: 'Incumbent, 32% share',
    receivedOffsetDays: -10,
    incoterm: 'Delivered, freight included',
    unitPrice: 85.03,
    freight: 0,
    tariff: 0,
    tariffPct: 0,
    moqUnits: 1_000,
    leadTimeWeeks: 4,
    paymentNetDays: 30,
    priceValidityDays: 120,
    priceHoldYears: 2,
    priceHoldNote: 'Year 3 follows aluminium producer prices, capped at 3%',
    capacityUnits: 52_000,
    changeNoticeDays: 120,
    ul1598: true,
    dlcPremium: true,
    iso9001: true,
    warrantyYears: 5,
    l70Hours: 100_000,
    recycledPct: 10,
    recyclabilityLabel: 'No packaging roadmap',
    uflpa: 'clear',
    uflpaDetail: 'US extrusion mill, supply chain mapped two steps back',
    optionalFeesAnnual: 48_000,
    feesLabel: '$48K per year account team, finish upcharges',
  },
  'sup-hanover': {
    supplierId: 'sup-hanover',
    roleLabel: 'New supplier',
    receivedOffsetDays: -9,
    incoterm: 'Delivered, freight included',
    unitPrice: 86.7,
    freight: 2.15,
    tariff: 0,
    tariffPct: 0,
    moqUnits: 5_000,
    leadTimeWeeks: 10,
    paymentNetDays: 45,
    priceValidityDays: 90,
    priceHoldYears: 1,
    priceHoldNote: 'Annual price review after year 1',
    capacityUnits: 64_000,
    changeNoticeDays: 180,
    ul1598: true,
    dlcPremium: true,
    iso9001: true,
    warrantyYears: 10,
    l70Hours: 120_000,
    recycledPct: 30,
    recyclabilityLabel: 'Foam-free pilot, available 2027',
    uflpa: 'clear',
    uflpaDetail: 'EU-sourced aluminium, supply chain mapped two steps back',
    optionalFeesAnnual: 0,
    feesLabel: 'None quoted',
  },
  'sup-brightpak': {
    supplierId: 'sup-brightpak',
    roleLabel: 'One-off supplier',
    receivedOffsetDays: -9,
    incoterm: 'Pillar collects in Shenzhen',
    unitPrice: 81.8,
    freight: 2.8,
    tariff: 24.54,
    tariffPct: 30,
    moqUnits: 10_500,
    leadTimeWeeks: 11,
    paymentNetDays: 30,
    priceValidityDays: 60,
    priceHoldYears: 1,
    priceHoldNote: 'Tariff changes passed through immediately',
    capacityUnits: 169_000,
    changeNoticeDays: 30,
    ul1598: false,
    dlcPremium: false,
    iso9001: true,
    certNote: 'UL and DLC Premium certificates expected Q1 2027',
    warrantyYears: 5,
    l70Hours: 60_000,
    recycledPct: 5,
    recyclabilityLabel: 'No recyclable packaging offer',
    uflpa: 'flagged',
    uflpaDetail: 'Direct supplier attested, its own suppliers not disclosed',
    optionalFeesAnnual: 0,
    feesLabel: 'None quoted',
  },
};

export function landedPerUnit(bid: BidFacts): number {
  return round2(bid.unitPrice + bid.freight + bid.tariff);
}

export const LOWEST_LANDED = Math.min(
  ...BIDDER_IDS.map((id) => landedPerUnit(BIDS[id])),
);

/** Three-year landed cost if this bidder took 100% of volume. */
export function threeYearCost(bid: BidFacts): number {
  return landedPerUnit(bid) * LED_VOLUME * CONTRACT_YEARS;
}

// ---------------------------------------------------------------------------
// Scorecards (0-10 per criterion). Landed cost and lead time come from the
// bid numbers; the rest are analyst scores with the reasoning attached.
// ---------------------------------------------------------------------------

export interface ScoreEntry {
  score: number;
  reason: string;
}

export type Scorecard = Record<CriterionId, ScoreEntry>;

const LOWEST_LEAD_TIME = Math.min(
  ...BIDDER_IDS.map((id) => BIDS[id].leadTimeWeeks),
);

function priceScoreEntry(id: BidderId): ScoreEntry {
  const bid = BIDS[id];
  const landed = landedPerUnit(bid);
  const gap = (landed / LOWEST_LANDED - 1) * 100;
  return {
    score: landedCostScore(landed, LOWEST_LANDED),
    reason:
      gap === 0
        ? `Lowest delivered cost at ${fmtPerUnit(landed)} per fixture.`
        : `Delivered ${fmtPerUnit(landed)} per fixture, ${gap.toFixed(1)}% above the lowest bid.`,
  };
}

function leadTimeEntry(id: BidderId): ScoreEntry {
  const weeks = BIDS[id].leadTimeWeeks;
  return {
    score: leadTimeScore(weeks, LOWEST_LEAD_TIME),
    reason: `${weeks} weeks from purchase order.`,
  };
}

export const SCORECARDS: Record<BidderId, Scorecard> = {
  'sup-aurelia': {
    landedCost: priceScoreEntry('sup-aurelia'),
    quality: {
      score: 9.4,
      reason:
        'UL and DLC Premium certified, ISO 9001 certified, 95 defects per million and 97% on time and in full over 12 months, a 10-year warranty and a commitment to give 180 days of notice before changes.',
    },
    supply: {
      score: 8.6,
      reason: `Declares ${fmtFixtures(BIDS['sup-aurelia'].capacityUnits)} per year across three assembly lines, with line 4 adding capacity in 2027. Can cover 100% of volume if needed. Clean forced-labor import check.`,
    },
    leadTime: leadTimeEntry('sup-aurelia'),
    sustainability: {
      score: 8.8,
      reason:
        'Sustainability score (EcoVadis) 82, foam-free cartons by 2028 and 25% recycled aluminium housings from 2027.',
    },
    commercial: {
      score: 9.0,
      reason:
        'Net 60 meets policy, 2% early-pay discount kept, three-year price hold. The aluminium price clause needs a cap.',
    },
  },
  'sup-northgate': {
    landedCost: priceScoreEntry('sup-northgate'),
    quality: {
      score: 8.2,
      reason:
        'UL and DLC Premium certified, ISO 9001, 160 defects per million and 93% on time and in full. Offers 120 days of notice before changes against a 180-day requirement.',
    },
    supply: {
      score: 7.4,
      reason: `One Akron plant. Declared capacity of ${fmtFixtures(BIDS['sup-northgate'].capacityUnits)} per year caps its share at 40%. Domestic supply, no tariff exposure.`,
    },
    leadTime: leadTimeEntry('sup-northgate'),
    sustainability: {
      score: 6.2,
      reason:
        'Sustainability score 61, no packaging roadmap and 10% recycled aluminium housings.',
    },
    commercial: {
      score: 6.0,
      reason:
        'Asks Net 30 against a Net 60 policy, adds a $48K per year account team and finish upcharges, holds price for two years.',
    },
  },
  'sup-hanover': {
    landedCost: priceScoreEntry('sup-hanover'),
    quality: {
      score: 7.6,
      reason:
        'UL and DLC Premium certified, ISO 9001 and 180 days of notice before changes. No performance history with Pillar and onboarding is still in risk review.',
    },
    supply: {
      score: 7.0,
      reason: `Declares ${fmtFixtures(BIDS['sup-hanover'].capacityUnits)} per year from a line installed in 2024. Single EU site and not yet onboarded.`,
    },
    leadTime: leadTimeEntry('sup-hanover'),
    sustainability: {
      score: 8.0,
      reason:
        'Sustainability score 67, foam-free packaging pilot available in 2027 and 30% recycled aluminium housings.',
    },
    commercial: {
      score: 6.8,
      reason:
        'Net 45, one-year price hold, 90-day validity and a 25% minimum volume commitment.',
    },
  },
  'sup-brightpak': {
    landedCost: priceScoreEntry('sup-brightpak'),
    quality: {
      score: 5.2,
      reason:
        'UL and DLC Premium certificates not yet held for the 150 W model, light output lasting only 60,000 hours to 70%, 410 defects per million and 88.5% on time and in full as a one-off supplier, 30 days of notice before changes.',
    },
    supply: {
      score: 4.6,
      reason: `Forced-labor import risk in its supply chain (RSK-307), 30% tariff exposure and an 11-week ocean lead time. Declares ${fmtFixtures(BIDS['sup-brightpak'].capacityUnits)} of capacity.`,
    },
    leadTime: leadTimeEntry('sup-brightpak'),
    sustainability: {
      score: 3.8,
      reason:
        'Sustainability score 44, no recyclable packaging offer and 5% recycled aluminium housings.',
    },
    commercial: {
      score: 4.0,
      reason:
        'Net 30, 60-day validity and a 12-month hold with immediate tariff pass-through.',
    },
  },
};

export function scoresFor(id: BidderId): Scores {
  const card = SCORECARDS[id];
  return {
    landedCost: card.landedCost.score,
    quality: card.quality.score,
    supply: card.supply.score,
    leadTime: card.leadTime.score,
    sustainability: card.sustainability.score,
    commercial: card.commercial.score,
  };
}

// ---------------------------------------------------------------------------
// Scenarios
// ---------------------------------------------------------------------------

export const SCENARIO_PRICES: Record<
  BidderId,
  { landed: number; tariff: number }
> = {
  'sup-aurelia': { landed: landedPerUnit(BIDS['sup-aurelia']), tariff: 0 },
  'sup-northgate': { landed: landedPerUnit(BIDS['sup-northgate']), tariff: 0 },
  'sup-hanover': { landed: landedPerUnit(BIDS['sup-hanover']), tariff: 0 },
  'sup-brightpak': {
    landed: landedPerUnit(BIDS['sup-brightpak']),
    tariff: BIDS['sup-brightpak'].tariff,
  },
};

export const SCENARIO_INPUTS = {
  volume: LED_VOLUME,
  baselinePerUnit: BASELINE_PER_UNIT,
  years: CONTRACT_YEARS,
};

export interface Scenario {
  id: string;
  name: string;
  tagline: string;
  shares: Shares;
  recommended?: boolean;
  riskNotes: string[];
}

export const SCENARIOS: Scenario[] = [
  {
    id: 'lowest-unit',
    name: 'Lowest unit price',
    tagline: 'Brightpak 100%',
    shares: {
      'sup-aurelia': 0,
      'sup-northgate': 0,
      'sup-hanover': 0,
      'sup-brightpak': 100,
    },
    riskNotes: [
      'Single source in China for the fixture in every Pillar Pro high-bay kit.',
      `A 30% tariff adds ${fmtPerUnit(BIDS['sup-brightpak'].tariff)} per fixture and lands above every other bid.`,
      'The forced-labor import risk (RSK-307) could stop imports if confirmed.',
    ],
  },
  {
    id: 'incumbent',
    name: 'Incumbent only',
    tagline: 'Aurelia 100%, also the best quality score',
    shares: {
      'sup-aurelia': 100,
      'sup-northgate': 0,
      'sup-hanover': 0,
      'sup-brightpak': 0,
    },
    riskNotes: [
      'Lowest delivered cost and highest quality score.',
      'One supplier for the whole item, above the 60% single-supplier cap.',
      'No approved alternate if the Monterrey plant is disrupted.',
    ],
  },
  {
    id: 'recommended',
    name: 'Recommended split',
    tagline: 'Aurelia 60% / Northgate 40%',
    shares: {
      'sup-aurelia': 60,
      'sup-northgate': 40,
      'sup-hanover': 0,
      'sup-brightpak': 0,
    },
    recommended: true,
    riskNotes: [
      'Both suppliers are approved to ship to Dallas, Phoenix and Hayward.',
      'Northgate sits at its declared 40% capacity ceiling.',
      'Needs Northgate to agree to 180 days of notice before changes in the contract.',
    ],
  },
  {
    id: 'new-entrant',
    name: 'New entrant mix',
    tagline: 'Aurelia 70% / Hanover 30%',
    shares: {
      'sup-aurelia': 70,
      'sup-northgate': 0,
      'sup-hanover': 30,
      'sup-brightpak': 0,
    },
    riskNotes: [
      'Hanover is not onboarded (ONB-0091 is in risk review).',
      `A ${BIDS['sup-hanover'].leadTimeWeeks}-week lead time and ${fmtFixtures(BIDS['sup-hanover'].moqUnits)} minimum order raise inventory at the DCs.`,
      'Drops Northgate, the only domestic source.',
    ],
  },
];

export const RECOMMENDED_SCENARIO: Scenario =
  SCENARIOS.find((s) => s.recommended) ?? SCENARIOS[0];

export const RECOMMENDED_RESULT = evaluateScenario(
  RECOMMENDED_SCENARIO.shares,
  SCENARIO_PRICES,
  SCENARIO_INPUTS,
);

const INCUMBENT_SCENARIO: Scenario =
  SCENARIOS.find((s) => s.id === 'incumbent') ?? SCENARIOS[0];

export const INCUMBENT_RESULT = evaluateScenario(
  INCUMBENT_SCENARIO.shares,
  SCENARIO_PRICES,
  SCENARIO_INPUTS,
);

/** Premium for using two suppliers versus Aurelia alone over the contract term. */
export const DUAL_SOURCE_PREMIUM =
  INCUMBENT_RESULT.savings - RECOMMENDED_RESULT.savings;

/** Annual spend by bidder under the recommended split. */
export function recommendedAnnualSpend(id: BidderId): number {
  return (
    (RECOMMENDED_SCENARIO.shares[id] / 100) *
    LED_VOLUME *
    landedPerUnit(BIDS[id])
  );
}

/** Annual fixtures by bidder under the recommended split. */
export function recommendedAnnualUnits(id: BidderId): number {
  return (RECOMMENDED_SCENARIO.shares[id] / 100) * LED_VOLUME;
}

// ---------------------------------------------------------------------------
// Negotiation value (Northgate drops the fee and moves to Net 45)
// ---------------------------------------------------------------------------

/** Account-team fee avoided over the term. */
export const FEE_AVOIDED_THREE_YEAR =
  BIDS['sup-northgate'].optionalFeesAnnual * CONTRACT_YEARS;

/** Cash released by Net 30 becoming Net 45 on Northgate's recommended share. */
export const NET45_WORKING_CAPITAL =
  recommendedAnnualSpend('sup-northgate') * (15 / 365);

/** Savings once the negotiated terms are applied. */
export const NEGOTIATED_SAVINGS =
  RECOMMENDED_RESULT.savings + FEE_AVOIDED_THREE_YEAR;

// ---------------------------------------------------------------------------
// Events table
// ---------------------------------------------------------------------------

export const SEED_EVENTS: SourcingEvent[] = [
  {
    id: RFP_ID,
    title: 'Pillar Pro LED high-bay fixtures 2027–2029',
    type: 'RFP',
    procurementType: 'direct',
    categoryId: 'cat-lighting',
    materialId: 'MAT-LED',
    status: 'ready_to_award',
    invited: 5,
    responded: 4,
    dateLabel: 'Award by',
    dateOffsetDays: 6,
    baselineAnnual: BASELINE_ANNUAL,
    savings: RECOMMENDED_RESULT.savings,
    savingsBasis: '3 yrs',
    ownerId: 'sarah-chen',
    statusNote: 'Bids in, 4 of 5 bidders',
    why: 'Both current LED agreements end in 2027, so bids from current and new suppliers set the price for 2027 to 2029.',
    origins: [
      {
        kind: 'agreement',
        ids: ['SA-AURE-2024-009', 'SA-NGTE-2024-012'],
      },
    ],
  },
  {
    id: RFQ_ID,
    title: 'Motor drive second source, 50 HP 480 V',
    type: 'RFQ',
    procurementType: 'direct',
    categoryId: 'cat-drives',
    materialId: 'MAT-VFD',
    status: 'evaluation',
    invited: 4,
    responded: 3,
    dateLabel: 'Evaluation closes',
    dateOffsetDays: 12,
    baselineAnnual: 18_700_000,
    savings: null,
    savingsBasis: null,
    ownerId: 'sarah-chen',
    statusNote: 'Resilience award',
    why: 'Lumen Drive is the only source of 50 HP motor drives and is billing a tariff surcharge its agreement forbids. A second source ends the dependence.',
    origins: [
      { kind: 'request', ids: ['REQ-1044'] },
      { kind: 'risk', ids: ['RSK-305'] },
    ],
  },
  {
    id: 'RFI-2026-007',
    title: 'Dry-type transformer second source',
    type: 'RFI',
    procurementType: 'direct',
    categoryId: 'cat-power',
    status: 'draft',
    invited: 6,
    responded: 0,
    dateLabel: 'Send on',
    dateOffsetDays: 3,
    baselineAnnual: null,
    savings: null,
    savingsBasis: null,
    ownerId: 'marcus-hale',
    requestId: 'REQ-1039',
    why: 'Stellar Transformer lead times are over 50 weeks. These questions ask other makers what they could supply.',
    origins: [
      { kind: 'request', ids: ['REQ-1039'] },
      { kind: 'risk', ids: ['RSK-296'] },
    ],
  },
  {
    // Drafted by the agent for REQ-1048. Numbered below RFP-2026-019 so the
    // next free number (RFP-2026-020) is still the one "Start bids" uses.
    id: 'RFP-2026-018',
    title: 'Pillar Pro launch agency',
    type: 'RFP',
    procurementType: 'indirect',
    categoryId: 'cat-marketing',
    status: 'draft',
    invited: 3,
    responded: 0,
    dateLabel: 'Send on',
    dateOffsetDays: 2,
    baselineAnnual: 326_000,
    savings: null,
    savingsBasis: null,
    ownerId: 'daniel-reyes',
    requestId: 'REQ-1048',
    why: 'Spend above $250K needs three competing bids, even with Brightline’s master agreement in place.',
    origins: [{ kind: 'request', ids: ['REQ-1048'] }],
    recommendation:
      'Invite Brightline and two new agencies. Policy needs three bids above $250K',
    nextStep: 'Procurement signs off, then send to 3 agencies',
  },
  {
    id: 'RFP-2026-019',
    title: 'Tariff and network resilience consulting',
    type: 'RFP',
    procurementType: 'indirect',
    categoryId: 'cat-prof-services',
    status: 'awarded',
    invited: 6,
    responded: 5,
    dateLabel: 'Awarded',
    dateOffsetDays: -176,
    baselineAnnual: 540_000,
    savings: 90_000,
    savingsBasis: 'one-time',
    ownerId: 'marcus-hale',
    awardedSupplierId: 'sup-halbrook',
    awardedContractId: 'SOW-HALB-2026-03',
    why: 'New duties on imported goods meant Pillar needed outside help to rework its supplier and freight plans.',
    origins: [{ kind: 'note', text: 'a tariff planning need' }],
  },
  {
    id: 'RFQ-2026-011',
    title: 'Corrugated cartons and pallets 2026',
    type: 'RFQ',
    procurementType: 'indirect',
    categoryId: 'cat-logistics',
    status: 'awarded',
    invited: 4,
    responded: 4,
    dateLabel: 'Awarded',
    dateOffsetDays: -232,
    baselineAnnual: 3_400_000,
    savings: 412_000,
    savingsBasis: '1 yr',
    ownerId: 'sarah-chen',
    awardedToName: 'Rivergate Corrugated Inc.',
    why: 'The carton and pallet agreement was ending and prices had not been tested in a year.',
    origins: [{ kind: 'note', text: 'the yearly packaging renewal' }],
  },
  {
    id: 'RFP-2025-027',
    title: 'Southwest partial-load freight routes',
    type: 'RFP',
    procurementType: 'indirect',
    categoryId: 'cat-logistics',
    status: 'awarded',
    invited: 7,
    responded: 5,
    dateLabel: 'Awarded',
    dateOffsetDays: -421,
    baselineAnnual: 4_800_000,
    savings: 860_000,
    savingsBasis: '2 yrs',
    ownerId: 'daniel-reyes',
    awardedToName: 'Sunbelt Line Haul Inc.',
    why: 'Rates on partial-load freight had drifted above the market.',
    origins: [{ kind: 'note', text: 'a freight cost review' }],
  },
  {
    id: 'RFP-2025-031',
    title: 'Contingent labor managed service',
    type: 'RFP',
    procurementType: 'indirect',
    categoryId: 'cat-contingent',
    status: 'awarded',
    invited: 6,
    responded: 5,
    dateLabel: 'Awarded',
    dateOffsetDays: -512,
    baselineAnnual: 9_090_000,
    savings: 1_200_000,
    savingsBasis: '3 yrs',
    ownerId: 'daniel-reyes',
    awardedSupplierId: 'sup-keystone',
    why: 'Several staffing agencies billed separately, and Pillar wanted one managed service.',
    origins: [{ kind: 'note', text: 'the staffing agency consolidation' }],
  },
];

export interface StatusMeta {
  label: string;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
}

export const STATUS_META: Record<SourcingStatus, StatusMeta> = {
  draft: { label: 'Draft by AI', tone: 'neutral' },
  published: { label: 'Published', tone: 'neutral' },
  evaluation: { label: 'Evaluation', tone: 'neutral' },
  ready_to_award: { label: 'Ready to award', tone: 'warning' },
  awarded: { label: 'Awarded', tone: 'success' },
};

export function bidderName(id: BidderId): string {
  return getSupplier(id).shortName;
}

/** First name of a supplier contact, for email salutations. */
export function contactFirstName(id: BidderId): string {
  return getSupplier(id).primaryContact.name.split(' ')[0];
}
